import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import HomePage from './pages/HomePage';
import Auth from './pages/Auth';
import PortalLayout from './components/portal/PortalLayout';
import Dashboard from './pages/portal/Dashboard.jsx';
import Stages from './pages/portal/Stages.jsx';
import ProgrammeDetails from './pages/portal/ProgrammeDetails.jsx';
import Tasks from './pages/portal/Tasks.jsx';
import Documents from './pages/portal/Documents.jsx';
import Apps from './pages/portal/Apps.jsx';
import News from './pages/portal/News.jsx';
import Settings from './pages/portal/Settings.jsx';
import BusinessDashboard from './pages/portal/BusinessDashboard.jsx';
import Team from './pages/portal/Team.jsx';
import Branding from './pages/portal/Branding';
import StageForm from './pages/StageForm';
import Report from './pages/Report';
import ChooseCoach from './pages/ChooseCoach';
import Pricing from './pages/Pricing';
import SpringboardAccess from './pages/SpringboardAccess';
import FitQuiz from './pages/FitQuiz'; // REMOVED - kept import to avoid build break, component unused
import Stage1Assessment from './pages/Stage1Assessment';
import AssessmentFlow from './pages/assessment/AssessmentFlow';
import AssessmentResults from './pages/assessment/AssessmentResults';
import FeedbackPage from './pages/assessment/FeedbackPage';
import AssessmentRegister from './pages/assessment/AssessmentRegister';
import AdminAssessments from './pages/assessment/AdminAssessments';
import PortalPreview from './pages/PortalPreview';
import SpringHome from './pages/SpringHome';
import AboutPage from './pages/AboutPage';
import PathwaysPage from './pages/PathwaysPage';
import NavBar from './components/NavBar';
import AdminSection from './pages/AdminSection';
import HostDashboard from './pages/HostDashboard';
import Results from './pages/Results';
import PathwayAssignment from './pages/PathwayAssignment';
import Badge from './pages/Badge';
import PaymentSuccess from './pages/PaymentSuccess';
import './App.css';
import ScrollIndicator from './components/ScrollIndicator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('spring_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('spring_token');
        setToken(null);
        setUser(null);
      }
      setLoading(false);
    };

    verifyToken();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('spring_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, avatar) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password, avatar });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('spring_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('spring_token');
    setToken(null);
    setUser(null);
  };

  const updateUser = (newData) => {
    setUser(prev => ({ ...prev, ...newData }));
  };

  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (e) { /* ignore */ }
  };

  const loginWithToken = (newToken, userData) => {
    localStorage.setItem('spring_token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, updateUser, refreshUser, loginWithToken }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, skipCoachCheck }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse">
          <span className="text-xl headline text-black">SPRING</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Redirect to coach selection if no coach picked yet (unless already on that page)
  if (!skipCoachCheck && !user.coach) {
    return <Navigate to="/choose-coach" replace />;
  }

  return children;
};

// Public Route
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse">
          <span className="text-xl headline text-black">SPRING</span>
        </div>
      </div>
    );
  }

  if (user && !user.coach) {
    return <Navigate to="/choose-coach" replace />;
  }

  if (user) {
    return <Navigate to="/portal" replace />;
  }

  return children;
};

// Every new page opens at the top, so its heading is never left under the nav bar
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-white">
          <ScrollToTop />
          <NavBar />
          <ScrollIndicator />
          <Routes>
            <Route path="/" element={<SpringHome />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/pathways" element={<PathwaysPage />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/springboard" element={<SpringboardAccess />} />
            <Route path="/quiz" element={<FitQuiz />} />
            <Route path="/assessment" element={<AssessmentFlow />} />
            <Route path="/assessment/results/:attemptId" element={<AssessmentResults />} />
            <Route path="/assessment/feedback/:attemptId" element={<FeedbackPage />} />
            <Route path="/assessment/register/:attemptId" element={<AssessmentRegister />} />
            <Route path="/assessment/accepted/:attemptId" element={<AssessmentResults />} />
            <Route path="/admin/assessments" element={<AdminAssessments />} />
            <Route path="/admin" element={<AdminSection />} />
            <Route path="/host" element={<HostDashboard />} />
            <Route path="/portal-preview" element={<PortalPreview />} />
            <Route path="/stage1-assessment" element={<ProtectedRoute><Stage1Assessment /></ProtectedRoute>} />
            <Route path="/results" element={<ProtectedRoute><Results /></ProtectedRoute>} />
            <Route path="/pathway" element={<ProtectedRoute><PathwayAssignment /></ProtectedRoute>} />
            <Route path="/badge" element={<ProtectedRoute><Badge /></ProtectedRoute>} />
            <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
            <Route
              path="/auth"
              element={
                <PublicRoute>
                  <Auth />
                </PublicRoute>
              }
            />
            {/* Coach Selection (after registration) */}
            <Route
              path="/choose-coach"
              element={
                <ProtectedRoute skipCoachCheck>
                  <ChooseCoach />
                </ProtectedRoute>
              }
            />
            {/* Portal Routes */}
            <Route
              path="/portal"
              element={
                <ProtectedRoute>
                  <PortalLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="stages" element={<Stages />} />
              <Route path="programme" element={<ProgrammeDetails />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="documents" element={<Documents />} />
              <Route path="apps" element={<Apps />} />
              <Route path="news" element={<News />} />
              <Route path="business-dashboard" element={<BusinessDashboard />} />
              <Route path="branding" element={<Branding />} />
              <Route path="settings" element={<Settings />} />
              <Route path="team" element={<Team />} />
            </Route>
            {/* Stage Forms (all stages use the form-based UX) */}
            <Route
              path="/stage/:stageNum"
              element={
                <ProtectedRoute>
                  <StageForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/report"
              element={
                <ProtectedRoute>
                  <Report />
                </ProtectedRoute>
              }
            />
            {/* Legacy redirect */}
            <Route path="/dashboard" element={<Navigate to="/portal" replace />} />
            <Route path="/conversation" element={<Navigate to="/portal/stages" replace />} />
          </Routes>
          <Toaster position="top-center" />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
